import React, { useCallback, useEffect, useState } from "react";
import {
  CheckIcon,
  createValidationSchema,
  PencilSquareIcon,
  useCustomForm,
  validate,
  XMarkIcon,
} from "@";

interface EditableTextCellProps {
  id: string;
  value?: string;
  onSave: (next: string) => Promise<void>;
  placeholder?: string;
  children?: React.ReactNode;
  className?: string;
}

interface FormValues {
  name: string;
}

const schema = createValidationSchema({
  name: validate("req").max(100),
});

const EditableTextCell = ({
  value,
  onSave,
  placeholder = "—",
  children,
  className,
}: EditableTextCellProps) => {
  const [isEditing, setIsEditing] = useState(false);

  const { register, errors, handleSubmit, reset, setFocus, isSubmitting } =
    useCustomForm({ name: value ?? "" }, schema);

  useEffect(() => {
    if (!isEditing) reset({ name: value ?? "" });
  }, [value, isEditing, reset]);

  useEffect(() => {
    if (isEditing) setFocus("name");
  }, [isEditing, setFocus]);

  const cancel = useCallback(() => {
    reset({ name: value ?? "" });
    setIsEditing(false);
  }, [reset, value]);

  const onSubmit = async ({ name }: FormValues) => {
    const next = name.trim();
    if (next === (value ?? "")) {
      setIsEditing(false);
      return;
    }
    try {
      await onSave(next);
      setIsEditing(false);
    } catch {
      reset({ name: value ?? "" });
    }
  };

  return (
    <div className="relative h-full w-full">
      {!isEditing && (
        <button
          type="button"
          onClick={() => setIsEditing(true)}
          title={value || "اضغط للتعديل"}
          className={`group flex h-full w-full max-w-full items-center gap-2 rounded-lg px-2 py-1.5 text-start transition-colors hover:bg-purple-50/80 dark:hover:bg-purple-950/20 ${
            className || ""
          }`}
        >
          {children}
          <span
            className={`min-w-0 flex-1 truncate ${
              value ? "" : "italic text-gray-400 dark:text-gray-500"
            }`}
          >
            {value || placeholder}
          </span>
          <PencilSquareIcon
            className="h-3.5 w-3.5 shrink-0 text-purple-500 opacity-0 transition-opacity group-hover:opacity-100"
            aria-hidden="true"
          />
        </button>
      )}

      {isEditing && (
        <form
          onSubmit={handleSubmit(onSubmit)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              cancel();
            }
          }}
          className="absolute -top-7 start-0 z-20 flex w-max min-w-[320px] max-w-[440px] flex-col gap-1.5 rounded-2xl border border-purple-200 bg-white p-2 shadow-lg shadow-purple-900/10 ring-2 ring-purple-500/20 dark:border-purple-800 dark:bg-gray-800 dark:ring-purple-500/30"
        >
          <div className="flex items-center gap-1.5">
            <input
              {...register("name")}
              disabled={isSubmitting}
              autoComplete="off"
              className={`block h-9 min-w-0 flex-1 rounded-lg border bg-white px-2.5 text-sm text-gray-900 outline-none transition focus:ring-2 disabled:opacity-60 dark:bg-gray-900 dark:text-gray-100 ${
                errors.name
                  ? "border-red-400 focus:border-red-500 focus:ring-red-500/30"
                  : "border-gray-200 focus:border-purple-500 focus:ring-purple-500/30 dark:border-gray-700"
              }`}
            />

            <button
              type="submit"
              aria-label="حفظ"
              title="حفظ (Enter)"
              disabled={isSubmitting}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-purple-900 text-white shadow-sm shadow-purple-700/30 transition hover:bg-purple-800 disabled:cursor-not-allowed disabled:bg-purple-300 dark:bg-purple-800 dark:hover:bg-purple-700"
            >
              {isSubmitting ? (
                <svg
                  className="h-4 w-4 animate-spin"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeOpacity="0.25"
                  />
                  <path
                    d="M22 12a10 10 0 0 1-10 10"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </svg>
              ) : (
                <CheckIcon className="h-4 w-4" />
              )}
            </button>

            <button
              type="button"
              onClick={cancel}
              aria-label="إلغاء"
              title="إلغاء (Esc)"
              disabled={isSubmitting}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 transition hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-gray-200"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>

          {errors.name?.message && (
            <p className="px-1 text-[11px] font-medium text-red-500">
              {String(errors.name.message)}
            </p>
          )}
        </form>
      )}
    </div>
  );
};

export default EditableTextCell;
